import type { CopyKey } from "../copy.ts";

/**
 * Core UI copy, Spanish (Mexico). One entry per core copy key; the comment
 * above each entry is the English source (`../copy.ts`).
 *
 * Translators: fill in the empty strings. Keep `{placeholders}` exactly as in
 * the English (same names, braces included) and keep symbols such as "✓ ",
 * " ↑" and " ↓". An empty string means "not translated yet": the UI then
 * falls back (see `makeCopy`), so a partly filled file is safe to ship.
 */
export const copyEsMX: Record<CopyKey, string> = {
  // Designs
  "nav.catalog": "Diseños",
  // My designs
  "nav.mine": "Mis diseños",
  // Pick a design
  "catalog.title": "Elige un diseño",
  // No designs published yet.
  "catalog.empty": "Aún no hay diseños publicados.",
  // Parameters
  "configurator.params": "Parámetros",
  // Presets
  "configurator.presets": "Predefinidos",
  // Reset
  "configurator.reset": "Restablecer",
  // Download STL
  "configurator.export.stl": "Descargar STL",
  // Download 3MF
  "configurator.export.3mf": "Descargar 3MF",
  // Save
  "configurator.save": "Guardar",
  // Saved to My designs.
  "configurator.savedProfile": "Guardado en Mis diseños.",
  // View
  "configurator.savedView": "Ver",
  // Saved on this device. Sign in to keep it everywhere.
  "configurator.savedDevice": "Guardado en este dispositivo. Inicia sesión para tenerlo en todas partes.",
  // Copy link
  "configurator.share": "Copiar enlace",
  // Update to {version}
  "configurator.update": "Actualizar a {version}",
  // Building…
  "configurator.building": "Generando…",
  // Print check
  "configurator.warnings": "Revisión de impresión",
  // My designs
  "mine.title": "Mis diseños",
  // Nothing saved yet. Configure a design and press Save.
  "mine.empty": "Aún no has guardado nada. Configura un diseño y presiona Guardar.",
  // Sign in
  "auth.signIn": "Iniciar sesión",
  // Sign out
  "auth.signOut": "Cerrar sesión",
  // Sign in to save designs to your profile.
  "auth.required": "Inicia sesión para guardar diseños en tu cuenta.",
  // Sign in to download
  "auth.signInToExport": "Inicia sesión para descargar",
  // Geometry is generated in your browser; nothing is uploaded until you save.
  "footer.madeWith": "La geometría se genera en tu navegador; no se sube nada hasta que guardas.",
  // That doesn't look like a valid reference.
  "fits.invalid": "Esa referencia no parece válida.",
  // Up to {max} tags.
  "fits.max": "Hasta {max} etiquetas.",
  // I've printed this and it fits
  "fits.printed": "Ya lo imprimí y encaja bien",
  // Community configurations
  "community.title": "Configuraciones de la comunidad",
  // Reason
  "community.reportReason": "Motivo",
  // Details (optional)
  "community.reportDetails": "Detalles (opcional)",
  // This link has expired or does not exist
  "share.missing": "Este enlace expiró o no existe",
  // Ask for a new link, or start from a design.
  "share.missingBody": "Pide un enlace nuevo o empieza desde un diseño.",
  // Browse designs
  "share.browse": "Ver diseños",
  // (empty in English: optional line, shown only when a brand sets it)
  "catalog.eyebrow": "",
  // Delete
  "mine.delete": "Eliminar",
  // Delete \"{name}\"? This can't be undone.
  "mine.deleteConfirm": "¿Eliminar \"{name}\"? Esta acción no se puede deshacer.",
  // On this device
  "mine.onDevice": "En este dispositivo",
  // Cancel
  "mine.cancel": "Cancelar",
  // Rename
  "mine.rename": "Renombrar",
  // Rename {name}
  "mine.renameLabel": "Renombrar {name}",
  // Name
  "mine.nameField": "Nombre",
  // Save name
  "mine.renameSave": "Guardar nombre",
  // Leave it empty to name it by its dimensions.
  "mine.renameHint": "Déjalo vacío para nombrarlo por sus medidas.",
  // Code: {code}
  "mine.codeHint": "Código: {code}",
  // Nothing tagged here yet.
  "community.empty": "Aún no hay nada etiquetado aquí.",
  // Thanks for confirming
  "community.confirmed": "Impresión registrada. ¡Gracias!",
  // Report
  "community.report": "Reportar",
  // Reported. Thanks, we'll take a look.
  "community.reported": "Reportado. Gracias, lo revisaremos.",
  // Which {label} does it fit?
  "community.publishFitLabel": "¿Para qué {label} es?",
  // Cancel
  "community.cancel": "Cancelar",
  // See it in the community
  "community.viewInCommunity": "Ver en la comunidad",
  // Tag a {label} it fits…
  "community.tag": "Etiquetar un {label} al que le queda…",
  // Tag it for the community
  "community.tagTitle": "Etiquétalo para la comunidad",
  // Tell others which thing these parameters fit. Your name shows next to it; you can remove it any time.
  "community.tagHint": "Cuéntale a los demás a qué le quedan estos parámetros. Tu nombre aparece junto a la etiqueta; puedes quitarla cuando quieras.",
  // Tag
  "community.tagSubmit": "Etiquetar",
  // Pick or type a {label} first.
  "community.tagNeedsFit": "Primero elige o escribe un {label}.",
  // Not tagged yet: it needs your public name. Your set is still here; press Tag to try again.
  "community.tagNeedsName": "Aún no se etiquetó: necesita tu nombre público. Tu set sigue aquí; presiona Etiquetar para intentarlo de nuevo.",
  // Tagged {refs}.
  "community.tagged": "Etiquetaste {refs}.",
  // Tag another {label}
  "community.tagAnother": "Etiquetar otro {label}",
  // Already in the community for {refs}.
  "community.alreadyTagged": "Ya está en la comunidad para {refs}.",
  // by {name}
  "community.taggedBy": "de {name}",
  // Tagged by {names}
  "community.taggers": "Etiquetado por {names}",
  // your tag
  "community.yourTag": "tu etiqueta",
  // Remove your tag {ref}
  "community.removeTag": "Quitar tu etiqueta {ref}",
  // Community
  "community.tab": "Comunidad",
  // {count} tagged
  "community.count": "{count} etiquetados",
  // 1 tagged
  "community.countOne": "1 etiquetado",
  // Named after its parameters.
  "configurator.nameHint": "Se nombra según sus parámetros.",
  // Renamed to {name}
  "configurator.renamed": "Nuevo nombre: {name}",
  // Start from a {label}
  "configurator.startFromSet": "Empezar desde un {label}",
  // Use its dimensions
  "configurator.startFromSetUse": "Usar sus medidas",
  // Starts from the dimensions the community tagged for it. Check them against yours before printing.
  "configurator.startFromSetHint": "Parte de las medidas que la comunidad etiquetó para él. Compáralas con las tuyas antes de imprimir.",
  // Nothing tagged for {ref} here yet.
  "configurator.startFromSetNone": "Aún no hay nada etiquetado para {ref} aquí.",
  // Started from the dimensions tagged for {ref}.
  "configurator.startedFromSet": "Se usaron las medidas etiquetadas para {ref}.",
  // New design
  "configurator.new": "Nuevo diseño",
  // Start a new design from what's on the model. The saved one stays as it is.
  "configurator.newHint": "Empieza un diseño nuevo a partir de lo que está en el modelo. El guardado se queda como está.",
  // Started a new design from these parameters.
  "configurator.startedNew": "Empezaste un diseño nuevo con estos parámetros.",
  // Favorite
  "community.favorite": "Favorito",
  // Added to Favorites in My designs.
  "community.favorited": "Agregado a Favoritos en Mis diseños.",
  // Add a {label} it fits
  "community.addFit": "Agregar un {label} al que le queda",
  // I printed it for {ref}
  "community.confirmFitLabel": "Lo imprimí para {ref}",
  // No prints yet
  "community.fitUnconfirmed": "Aún sin impresiones",
  // 1 print
  "community.fitPrints.one": "1 impresión",
  // {count} prints
  "community.fitPrints.other": "{count} impresiones",
  // Printed by 1 person
  "community.printers.one": "Impreso por 1 persona",
  // Printed by {count} people
  "community.printers.other": "Impreso por {count} personas",
  // You already registered this print.
  "community.alreadyRegistered": "Ya registraste esta impresión.",
  // I printed it for this
  "community.addFitPrinted": "Lo imprimí para esto",
  // Add
  "community.addFitSubmit": "Agregar",
  // Type a {label} first.
  "community.addFitNeedsRef": "Escribe primero un {label}.",
  // You added {refs}. Thanks!
  "community.addedFit": "Agregaste {refs}. ¡Gracias!",
  // It already lists {refs}.
  "community.alreadyHasFit": "Ya tenía {refs}.",
  // Favorites
  "mine.liked": "Favoritos",
  // Yours
  "mine.yours": "Tuyos",
  // Your tags
  "mine.tagged": "Tus etiquetas",
  // Nothing tagged yet. Tag what a design fits from its Parameters tab.
  "mine.taggedEmpty": "Aún no etiquetas nada. Etiqueta a qué le queda un diseño desde su pestaña Parámetros.",
  // No favorites yet. Use ☆ Favorite on a community design.
  "mine.favoritesEmpty": "Aún no tienes favoritos. Usa ☆ Favorito en un diseño de la comunidad.",
  // Search by reference, name or person
  "community.searchLabel": "Busca por referencia, nombre o persona",
  // Sort
  "community.sortLabel": "Ordenar",
  // Newest
  "community.sort.newest": "Más recientes",
  // Most downloaded
  "community.sort.downloads": "Más descargadas",
  // Most printed
  "community.sort.confirmed": "Más impresas",
  // Most saved
  "community.sort.likes": "Más guardadas",
  // Name
  "community.sort.name": "Nombre",
  // Nothing matches “{query}”.
  "community.noResults": "Nada coincide con “{query}”.",
  // Tag what a design fits from the Parameters tab to list it here.
  "community.emptyHint": "Etiqueta a qué le queda un diseño desde la pestaña Parámetros para que aparezca aquí.",
  // Back to mine
  "community.restore": "Volver a lo mío",
  // A community design is on the model, so the parameters are locked.
  "community.locked": "Hay un diseño de la comunidad en el modelo; los parámetros están bloqueados.",
  // Report {ref}
  "community.reportTag": "Reportar {ref}",
  // I printed it
  "community.confirmFitShort": "Lo imprimí",
  // What it fits
  "community.setsLabel": "A qué le queda",
  // Community design on the model
  "community.onModelLabel": "Diseño de la comunidad en el modelo",
  // {count} tags
  "community.sets.other": "{count} etiquetas",
  // 1 tag
  "community.sets.one": "1 etiqueta",
  // ✓ You printed it
  "community.youPrinted": "✓ Lo imprimiste",
  // Made for {design}
  "community.madeFor": "Hecha para {design}",
  // Copy link
  "community.copyLink": "Copiar enlace",
  // Link copied
  "community.linkCopied": "Enlace copiado",
  // 1 download
  "downloads.one": "1 descarga",
  // {count} downloads
  "downloads.other": "{count} descargas",
  // Variant
  "family.label": "Variante",
  // Your parameters carry over.
  "family.hint": "Tus parámetros se conservan.",
  // Type a reference and press Enter, or pick a suggestion.
  "fits.hint": "Escribe una referencia y presiona Enter, o elige una sugerencia.",
  // Suggestions
  "fits.suggestions": "Sugerencias",
  // Searching…
  "fits.searching": "Buscando…",
  // Display name
  "profile.displayName": "Nombre público",
  // Choose your public name
  "profile.dialogTitle": "Elige tu nombre público",
  // It's shown next to the sets you tag. Your email and Google name never are.
  "profile.prompt": "Se muestra junto a los sets que etiquetas. Tu correo y tu nombre de Google nunca.",
  // Use this name
  "profile.save": "Usar este nombre",
  // Moderation queue
  "mod.title": "Cola de moderación",
  // No reported entries.
  "mod.empty": "No hay publicaciones reportadas.",
  // Loading…
  "common.loading": "Cargando…",
  // Retry
  "common.retry": "Reintentar",
  // Dismiss
  "common.dismiss": "Cerrar",
  // This deployment has no API configured.
  "common.noApi": "Esta instalación no tiene una API configurada.",
  // Primary
  "nav.primaryLabel": "Principal",
  // Skip to content
  "a11y.skipToContent": "Ir al contenido",
  // Language
  "locale.label": "Idioma",
  // Account
  "auth.account": "Cuenta",
  // Signed in as {email}
  "auth.signedInAs": "Sesión iniciada como {email}",
  // Loading designs…
  "catalog.loading": "Cargando diseños…",
  // Could not load the catalog
  "catalog.error": "No se pudo cargar el catálogo",
  // Run \"pnpm design build packs/<pack>\" to publish designs locally.
  "catalog.emptyBody": "Ejecuta \"pnpm design build packs/<pack>\" para publicar diseños localmente.",
  // Configure
  "card.configure": "Configurar",
  // View
  "card.view": "Ver",
  // Fits {label}
  "card.fitsLabel": "Para {label}",
  // Fits
  "card.fits": "Para",
  // Preparing…
  "configurator.preparing": "Preparando…",
  // Exporting…
  "configurator.exporting": "Exportando…",
  // Saving…
  "configurator.saving": "Guardando…",
  // ✓ Downloaded
  "configurator.done.export": "✓ Descargado",
  // ✓ Saved
  "configurator.done.save": "✓ Guardado",
  // ✓ Link copied
  "configurator.done.share": "✓ Enlace copiado",
  // My design
  "configurator.saveHeading": "Mi diseño",
  // 3D preview of {title}
  "configurator.viewport": "Vista previa 3D de {title}",
  // building…
  "configurator.busy": "generando…",
  // Min {value}
  "configurator.clampedMin": "Mín. {value}",
  // Max {value}
  "configurator.clampedMax": "Máx. {value}",
  // {input} is below the minimum, set to {value}
  "configurator.clampedToMin": "{input} está por debajo del mínimo, se ajustó a {value}",
  // {input} is above the maximum, set to {value}
  "configurator.clampedToMax": "{input} está por encima del máximo, se ajustó a {value}",
  // Range {min}–{max}
  "configurator.range": "Rango {min}–{max}",
  // Loading design…
  "configurator.loading": "Cargando diseño…",
  // Downloaded {filename} ({size})
  "configurator.live.exported": "Se descargó {filename} ({size})",
  // Link copied: {url}
  "configurator.live.linkCopied": "Enlace copiado: {url}",
  // Copy this link: {url}
  "configurator.live.linkReady": "Copia este enlace: {url}",
  // Could not open this design
  "configurator.openError": "No se pudo abrir este diseño",
  // Design not found
  "configurator.notFound": "Diseño no encontrado",
  // {design} is not in this catalog.
  "configurator.notFoundBody": "{design} no está en este catálogo.",
  // Design version pinned for this configuration
  "configurator.versionHint": "Versión del diseño fijada para esta configuración",
  // deprecated
  "configurator.deprecated": "obsoleto",
  // Print notes.
  "configurator.printNotes": "Notas de impresión.",
  // {tris} tris · {ms} ms
  "configurator.telemetry": "{tris} triángulos · {ms} ms",
  // General
  "configurator.groupGeneral": "General",
  // 3D viewport
  "viewport.role": "Visor 3D",
  // 3D preview
  "viewport.label": "Vista previa 3D",
  // Drag to rotate · scroll to zoom
  "viewport.orbitHint": "Arrastra para girar · desplaza para acercar",
  // Drag to rotate · pinch to zoom
  "viewport.orbitHintTouch": "Arrastra para girar · pellizca para acercar",
  // Dismiss
  "viewport.orbitHintDismiss": "Cerrar",
  // Dimensions
  "viewport.dimensions": "Cotas",
  // None
  "viewport.dimensionsNone": "Ninguna",
  // Main
  // All
  "viewport.dimensionsAll": "Todas",
  // Name
  "fits.nameLabel": "Nombre",
  // Could not load community configurations
  "community.error": "No se pudieron cargar las configuraciones de la comunidad",
  // Wrong fit
  "community.reason.wrong-fit": "No encaja",
  // Spam
  "community.reason.spam": "Spam",
  // Offensive
  "community.reason.offensive": "Ofensivo",
  // Other
  "community.reason.other": "Otro",
  // Sharing needs the API
  "share.noApi": "Compartir requiere la API",
  // Opening shared design…
  "share.opening": "Abriendo diseño compartido…",
  // change
  "profile.change": "cambiar",
  // 3–30 letters, digits, _ . -
  "profile.invalid": "3–30 letras, números, _ . -",
  // Delete {name}
  "mine.deleteLabel": "Eliminar {name}",
  // Staff only
  "mod.staffOnly": "Solo para el equipo",
  // Remove this tag? Prints registered for it go with it.
  "mod.removeConfirm": "¿Quitar esta etiqueta? Se van con ella las impresiones registradas para ella.",
  // {count} report(s) since {date}
  "mod.reports": "{count} reporte(s) desde {date}",
  // view
  "mod.view": "ver",
  // Hide
  "mod.hide": "Ocultar",
  // Restore
  "mod.restore": "Restaurar",
  // Dismiss
  "mod.dismiss": "Descartar",
  // Clear the reports and keep it live
  "mod.dismissHint": "Borrar los reportes y mantenerla visible",
  // Remove
  "mod.remove": "Eliminar",
  // live
  "mod.status.live": "visible",
  // hidden
  "mod.status.hidden": "oculta",
  // Find what fits yours
  "home.findLabel": "Encuentra lo que le queda al tuyo",
  // Type a reference or a name
  "home.findPlaceholder": "Escribe una referencia o un nombre",
  // Find
  "home.findSubmit": "Buscar",
  // Clear
  "home.clear": "Borrar",
  // Designs tagged for {ref}
  "home.resultsTitle": "Diseños etiquetados para {ref}",
  // 1 community version for it
  "home.entries.one": "1 versión de la comunidad",
  // {count} community versions for it
  "home.entries.other": "{count} versiones de la comunidad",
  // Nothing tagged for {ref} yet
  "home.noneTitle": "Aún nada etiquetado para {ref}",
  // Open a design below, adjust it until it fits, and tag it with {ref} from the Parameters tab so the next person finds it here.
  "home.noneBody": "Abre un diseño de abajo, ajústalo hasta que quede y etiquétalo con {ref} desde la pestaña Parámetros para que la próxima persona lo encuentre aquí.",
  // Could not search the community
  "home.error": "No se pudo buscar en la comunidad",
  // All designs
  "home.allTitle": "Todos los diseños",
  // No tagged set matches “{query}”. Type its reference number, or size a design to it below and tag it.
  "home.noMatch": "Ningún set etiquetado coincide con “{query}”. Escribe su número de referencia, o ajusta un diseño de abajo y etiquétalo.",
  // Customize your own ↓
  "home.noMatchCustomize": "Personaliza el tuyo ↓",
  // We couldn't find that. Try its reference number, or customize your own below.
  "home.invalid": "No lo encontramos. Prueba con su número de referencia, o personaliza el tuyo abajo.",
  // Try: 
  // or customize your own
  "home.orCustomize": "o personaliza el tuyo",
  // Only versions tagged for
  "community.fitFilter": "Solo versiones etiquetadas para",
  // Show all
  "community.fitFilterClear": "Ver todo",
  // Nothing in this design is tagged for {ref} yet.
  "community.fitNone": "En este diseño aún nada está etiquetado para {ref}.",
  // Next steps
  "next.title": "Siguientes pasos",
  // Downloaded {filename} ({size})
  "next.downloaded": "Descargaste {filename} ({size})",
  // Print it
  "next.printTitle": "Imprímelo",
  // Orientation
  "next.tip.orientation": "Orientación",
  // Supports
  "next.tip.supports": "Soportes de impresión",
  // Needed
  "next.tip.supportsYes": "Necesarios",
  // None
  "next.tip.supportsNo": "Ninguno",
  // Layer height
  "next.tip.layerHeight": "Altura de capa",
  // Infill
  "next.tip.infill": "Relleno",
  // Walls
  "next.tip.walls": "Paredes",
  // Material
  "next.tip.material": "Material",
  // You'll need
  "next.tip.hardware": "Necesitarás",
  // At most {grams} g of PLA ({volume} cm³ if printed solid).
  "next.filament": "Como máximo {grams} g de PLA ({volume} cm³ si se imprime sólido).",
  // Printed it?
  "next.printedTitle": "¿Ya lo imprimiste?",
  // Tell others it fits, so the next person can trust these dimensions.
  "next.printedHint": "Cuéntale a los demás que queda, para que la próxima persona confíe en estas medidas.",
  // I printed it for {ref}
  "next.printedFor": "Lo imprimí para {ref}",
  // I printed it: tag what it fits
  "next.printedTag": "Lo imprimí: etiqueta a qué le queda",
  // Which one did you print it for?
  "next.printedPick": "¿Para cuál lo imprimiste?",
  // ✓ Your print for {ref} is registered. Thanks!
  "next.printedDone": "✓ Tu impresión para {ref} quedó registrada. ¡Gracias!",
  // Sign in to say you printed it
  "next.signIn": "Inicia sesión para decir que lo imprimiste",
  // Save it, to tweak it after a test print
  "next.save": "Guárdalo para ajustarlo después de una impresión de prueba",
  // You downloaded {name} on {date}. Did it fit?
  "next.reminder": "Descargaste {name} el {date}. ¿Le quedó?",
  // Yes, I printed it
  "next.reminderYes": "Sí, lo imprimí",
  // Not quite, adjust it
  "next.reminderNo": "No del todo, ajustarlo",
  // Your downloaded dimensions are on the model: adjust them and download again.
  "next.adjusting": "Las medidas que descargaste están en el modelo: ajústalas y descarga de nuevo.",
  // We can't reach the server. Check your connection and try again.
  "error.offline": "No podemos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.",
  // Sign in to open this.
  "error.signIn": "Inicia sesión para abrir esto.",
  // It isn't here any more, or the link is wrong.
  "error.notFound": "Ya no está aquí o el enlace no es correcto.",
  // Something went wrong on our side. Try again in a moment.
  "error.generic": "Algo salió mal de nuestro lado. Inténtalo de nuevo en un momento.",
  // Start from this one
  "community.adopt": "Empezar desde este",
  // These dimensions are yours now: change them, save or tag them. The community's entry stays as it is.
  "community.adopted": "Estas medidas ahora son tuyas: cámbialas, guárdalas o etiquétalas. La entrada de la comunidad no cambia.",
  // ✓ 1 print for this set
  "home.prints.one": "✓ 1 impresión para este set",
  // ✓ {count} prints for this set
  "home.prints.other": "✓ {count} impresiones para este set",
  // Privacy
  "footer.privacy": "Privacidad",
  // Terms
  "footer.terms": "Términos",
  // Contact
  "footer.contact": "Contacto",
  // Removed your tag {ref}.
  "community.tagRemoved": "Quitaste tu etiqueta {ref}.",
  // {ref} is your tag again.
  "community.tagRestored": "{ref} vuelve a ser tu etiqueta.",
  // Undo
  "community.undo": "Deshacer",
  // {ref} is this entry's only set: removing your tag removes the entry from the community, prints included.
  "community.removeLast": "{ref} es el único set de esta entrada: al quitar tu etiqueta, la entrada sale de la comunidad, con sus impresiones.",
  // 1 other person registered a print for {ref}; removing your tag removes it too.
  "community.removePrinted.one": "Otra persona registró una impresión para {ref}; al quitar tu etiqueta también se quita.",
  // {count} other people registered prints for {ref}; removing your tag removes them too.
  "community.removePrinted.other": "Otras {count} personas registraron impresiones para {ref}; al quitar tu etiqueta también se quitan.",
  // Remove
  "community.removeLastYes": "Quitar",
  // Keep it
  "community.removeLastNo": "Conservarla",
  // A few reports hide the tag until a moderator reviews it.
  "community.reportHint": "Unos cuantos reportes ocultan la etiqueta hasta que un moderador la revise.",
  // Community version on the model.
  "community.appliedLive": "Versión de la comunidad en el modelo.",
  // Save, copy link, tag
  "configurator.saveJumpHint": "Guardar, copiar enlace, etiquetar",
  // STL: works in every slicer; millimetres.
  "configurator.export.stlHint": "STL: funciona en cualquier laminador; milímetros.",
  // 3MF: keeps units and the part's name; best for PrusaSlicer, Bambu Studio and Cura.
  "configurator.export.3mfHint": "3MF: conserva las unidades y el nombre de la pieza; ideal para PrusaSlicer, Bambu Studio y Cura.",
  // STL works everywhere; 3MF keeps units and suits PrusaSlicer, Bambu Studio and Cura.
  "configurator.export.formatHint": "STL funciona en todos lados; 3MF conserva las unidades y va mejor con PrusaSlicer, Bambu Studio y Cura.",
  // Removed from Favorites.
  "community.unfavorited": "Se quitó de Favoritos.",
  // Remove
  "mine.unfavorite": "Quitar",
  // Remove {name} from Favorites
  "mine.unfavoriteLabel": "Quitar {name} de Favoritos",
  // How it works
  "home.howLabel": "Cómo funciona",
  // Find what yours is
  "home.step1": "Encuentra el tuyo",
  // Pick a version people printed
  "home.step2": "Elige una versión que otros imprimieron",
  // Download & print
  "home.step3": "Descarga e imprime",
  // Free STL and 3MF files
  "home.free": "Archivos STL y 3MF gratis",
  // Free STL and 3MF files · sign in with Google to download
  "home.freeSignIn": "Archivos STL y 3MF gratis · inicia sesión con Google para descargar",
  // {count} references already tagged by the community
  "home.proof": "{count} referencias ya etiquetadas por la comunidad",
};
